import { datadogRum } from '@datadog/browser-rum';
import banner from '@/assets/banner.txt?raw';

export const logMethods = ['debug', 'info', 'log', 'warn', 'error'] as const;
export type LogMethod = typeof logMethods[number];

const LogLevels = Object.freeze({
  verbose: -Infinity,
  debug: 10,
  info: 20,
  log: 30,
  warn: 40,
  error: 50,
  silent: +Infinity,
});

export type LogLevel = keyof typeof LogLevels;

const bannerStyle = [
  'color: #25d366',
  'font-size: .75em',
  'line-height: 1.1',
  'display: inline-block',
].join(';');

const logoStyle = [
  'display: inline-block',
  'padding: 0 .1em .2em .25em',
  'border: 1px solid #25d366',
  'border-radius: 9em 9em 0',
  'color: #25d366',
  'font-family: sans-serif',
  'font-weight: 700',
  'font-size: 1.15em',
  'line-height: 0.8',
].join(';');

const logLevelTemplateStyle = [
  'display: inline-block',
  'background: _BACKGROUND_',
  'margin-left: .75ch',
  'padding: 0 3px',
  'border-radius: 99em',
].join(';');

const defaultLogLevel: number = LogLevels.log;
let logLevel: number = defaultLogLevel;

function shouldLog(level: number) {
  return appSettings.VUE_APP_AMBIENTE !== 'prd' && level >= logLevel;
}

function shouldTrack() {
  return appSettings.VUE_APP_AMBIENTE === 'prd';
}

function trackerFactory<T extends unknown[]>(trackFn: (...args: T) => void) {
  return (...args: T) => {
    if (!shouldTrack()) return;

    trackFn(...args);
  };
}

const tracker = {
  action: trackerFactory(datadogRum.addAction),
  error: trackerFactory(datadogRum.addError),
};

type TrackerActionParams = Parameters<typeof tracker.action>
type TrackerErrorParams = Parameters<typeof tracker.error>

function logFactory(logName: LogMethod, color: string) {
  const level = LogLevels[logName];

  const logLevelStyle = logLevelTemplateStyle.replace('_BACKGROUND_', color);

  const formatArgs = ['%ca.%c<%s>', logoStyle, logLevelStyle, logName];
  const logFn = (...msg: unknown[]) => {
    if (!shouldLog(level)) return;

    // eslint-disable-next-line no-console
    console[logName](...formatArgs, ...msg);
  };

  logFn.andTrackAction = (...args: TrackerActionParams) => {
    logFn(...args);
    tracker.action(...args);
  };

  logFn.andTrackError = (...args: TrackerErrorParams) => {
    logFn(...args);
    tracker.error(...args);
  };

  return logFn;
}

const printBanner = () => {
  // eslint-disable-next-line no-console
  console.log(`%c${banner.replaceAll('%', '%%')}`, bannerStyle);
};

export default {
  levels: LogLevels,
  defaultLevel: defaultLogLevel,

  setLogLevel(level?: number) {
    logLevel = level ?? defaultLogLevel;
  },

  getLogLevel() {
    return logLevel;
  },

  debug: logFactory('debug', '#6663'),
  info: logFactory('info', '#0396'),
  log: logFactory('log', '#0606'),
  warn: logFactory('warn', '#9606'),
  error: logFactory('error', '#c306'),

  tracker,

  printBanner,
};
